import { z } from "zod"
import type { AppErrors } from "./error"

export type Locale = "de" | "en" | "fr" | "it"

// Helper for localized attributes
const LocalizedAttributeSchema = z.object({
  de: z.string().nullable(),
  en: z.string().nullable(),
  fr: z.string().nullable(),
  it: z.string().nullable()
})

const FragmentAssetGroupSchema = z.union([
  z.array(
    z.object({
      id: z.coerce.number(),
      kind: z.string(),
      context: z.string(),
      assets: z.array(
        z.object({
          id: z.coerce.number(),
          locale: z.string(),
          public_id: z.string()
        })
      )
    })
  ),
  z.null(),
  z.undefined()
])

export type AppErrorTranslations = {
  [key in Locale]: {
    [key in AppErrors]: string
  }
}

export type Notification = {
  id: string
  message: string
  timestamp: Date
  type: "info" | "error"
}

type TranslatableValue = string
export type GenericTranslationsObject = {
  [key: string]: TranslatableValue | GenericTranslationsObject
}

export type Translations = {
  [key in Locale]: {
    [key: string]: string
  }
}

export type LocalizedAttribute = z.infer<typeof LocalizedAttributeSchema>

// Path

const PathSchema = z.object({
  id: z.coerce.number(),
  challenges: z.array(z.coerce.number())
})

export type Path = z.infer<typeof PathSchema>

// Fragment

const BasicFragmentSchema = z.object({
  kind: z.literal("basic"),
  title: LocalizedAttributeSchema,
  text: LocalizedAttributeSchema
})

const AdvancedFragmentSchema = z.object({
  kind: z.literal("advanced"),
  title: LocalizedAttributeSchema,
  text: LocalizedAttributeSchema,
  url: z.string().nullable()
})

const OpenSesameFragmentSchema = z.object({
  kind: z.literal("open_sesame"),
  question: LocalizedAttributeSchema,
  solution: z.string()
})

const IframeFragmentSchema = z.object({
  kind: z.literal("iframe"),
  url: LocalizedAttributeSchema,
  title: LocalizedAttributeSchema,
  text: LocalizedAttributeSchema
})

//Postfinance

const PostfinanceSharedAttributes = {
  location_id: z.string().nullable(),
  google_maps_url: z.string().nullable(),
  letter: z.string(),
  detail_text: LocalizedAttributeSchema,
  promoter_text: LocalizedAttributeSchema
}

const PostfinanceMultipleChoiceFragmentSchema = z.object({
  ...PostfinanceSharedAttributes,
  kind: z.literal("postfinance_multiple_choice"),
  question: LocalizedAttributeSchema,
  solution: z.string(),
  option_1: LocalizedAttributeSchema,
  option_1_explanation: LocalizedAttributeSchema,
  option_2: LocalizedAttributeSchema,
  option_2_explanation: LocalizedAttributeSchema,
  option_3: LocalizedAttributeSchema,
  option_3_explanation: LocalizedAttributeSchema,
  option_4: LocalizedAttributeSchema,
  option_4_explanation: LocalizedAttributeSchema,
  fragment_asset_groups: FragmentAssetGroupSchema
})

// WDD

const WddSharedAttributes = {
  location_id: z.string().nullable(),
  google_maps_url: z.string().nullable(),
  points: z.coerce.number(),
  detail_text: LocalizedAttributeSchema,
  promoter_text: LocalizedAttributeSchema
}

const WddOpenSesameFragmentSchema = z.object({
  ...WddSharedAttributes,
  kind: z.literal("wdd_open_sesame"),
  question: LocalizedAttributeSchema,
  solution: z.string(),
  explanation: LocalizedAttributeSchema
})

const WddMultipleChoiceFragmentSchema = z.object({
  ...WddSharedAttributes,
  kind: z.literal("wdd_multiple_choice"),
  question: LocalizedAttributeSchema,
  solution: z.string(),
  option_1: LocalizedAttributeSchema,
  option_1_explanation: LocalizedAttributeSchema,
  option_2: LocalizedAttributeSchema,
  option_2_explanation: LocalizedAttributeSchema,
  option_3: LocalizedAttributeSchema,
  option_3_explanation: LocalizedAttributeSchema,
  option_4: LocalizedAttributeSchema,
  option_4_explanation: LocalizedAttributeSchema,
  fragment_asset_groups: FragmentAssetGroupSchema
})

const WddMultipleChoiceImageFragmentSchema = z.object({
  ...WddSharedAttributes,
  kind: z.literal("wdd_multiple_choice_image"),
  question: LocalizedAttributeSchema,
  solution: z.string(),
  explanation_correct: LocalizedAttributeSchema,
  explanation_incorrect: LocalizedAttributeSchema,
  fragment_asset_groups: FragmentAssetGroupSchema
})

const WddMultipleChoiceSequenceFragmentSchema = z.object({
  location_id: z.string().nullable(),
  google_maps_url: z.string().nullable(),
  detail_text: LocalizedAttributeSchema,
  promoter_text: LocalizedAttributeSchema,
  //
  kind: z.literal("wdd_multiple_choice_sequence"),
  points_1: z.coerce.number(),
  question_1: LocalizedAttributeSchema,
  solution_1: z.string(),
  question_1_option_1: LocalizedAttributeSchema,
  question_1_option_1_explanation: LocalizedAttributeSchema,
  question_1_option_2: LocalizedAttributeSchema,
  question_1_option_2_explanation: LocalizedAttributeSchema,
  question_1_option_3: LocalizedAttributeSchema,
  question_1_option_3_explanation: LocalizedAttributeSchema,
  question_1_option_4: LocalizedAttributeSchema,
  question_1_option_4_explanation: LocalizedAttributeSchema,
  points_2: z.coerce.number(),
  question_2: LocalizedAttributeSchema,
  solution_2: z.string(),
  question_2_option_1: LocalizedAttributeSchema,
  question_2_option_1_explanation: LocalizedAttributeSchema,
  question_2_option_2: LocalizedAttributeSchema,
  question_2_option_2_explanation: LocalizedAttributeSchema,
  question_2_option_3: LocalizedAttributeSchema,
  question_2_option_3_explanation: LocalizedAttributeSchema,
  question_2_option_4: LocalizedAttributeSchema,
  question_2_option_4_explanation: LocalizedAttributeSchema,
  points_3: z.coerce.number(),
  question_3: LocalizedAttributeSchema,
  solution_3: z.string(),
  question_3_option_1: LocalizedAttributeSchema,
  question_3_option_1_explanation: LocalizedAttributeSchema,
  question_3_option_2: LocalizedAttributeSchema,
  question_3_option_2_explanation: LocalizedAttributeSchema,
  question_3_option_3: LocalizedAttributeSchema,
  question_3_option_3_explanation: LocalizedAttributeSchema,
  question_3_option_4: LocalizedAttributeSchema,
  question_3_option_4_explanation: LocalizedAttributeSchema,
  points_4: z.coerce.number(),
  question_4: LocalizedAttributeSchema,
  solution_4: z.string(),
  question_4_option_1: LocalizedAttributeSchema,
  question_4_option_1_explanation: LocalizedAttributeSchema,
  question_4_option_2: LocalizedAttributeSchema,
  question_4_option_2_explanation: LocalizedAttributeSchema,
  question_4_option_3: LocalizedAttributeSchema,
  question_4_option_3_explanation: LocalizedAttributeSchema,
  question_4_option_4: LocalizedAttributeSchema,
  question_4_option_4_explanation: LocalizedAttributeSchema,
  points_5: z.coerce.number(),
  question_5: LocalizedAttributeSchema,
  solution_5: z.string(),
  question_5_option_1: LocalizedAttributeSchema,
  question_5_option_1_explanation: LocalizedAttributeSchema,
  question_5_option_2: LocalizedAttributeSchema,
  question_5_option_2_explanation: LocalizedAttributeSchema,
  question_5_option_3: LocalizedAttributeSchema,
  question_5_option_3_explanation: LocalizedAttributeSchema,
  question_5_option_4: LocalizedAttributeSchema,
  question_5_option_4_explanation: LocalizedAttributeSchema,
  points_6: z.coerce.number(),
  question_6: LocalizedAttributeSchema,
  solution_6: z.string(),
  question_6_option_1: LocalizedAttributeSchema,
  question_6_option_1_explanation: LocalizedAttributeSchema,
  question_6_option_2: LocalizedAttributeSchema,
  question_6_option_2_explanation: LocalizedAttributeSchema,
  question_6_option_3: LocalizedAttributeSchema,
  question_6_option_3_explanation: LocalizedAttributeSchema,
  question_6_option_4: LocalizedAttributeSchema,
  question_6_option_4_explanation: LocalizedAttributeSchema,
  points_7: z.coerce.number(),
  question_7: LocalizedAttributeSchema,
  solution_7: z.string(),
  question_7_option_1: LocalizedAttributeSchema,
  question_7_option_1_explanation: LocalizedAttributeSchema,
  question_7_option_2: LocalizedAttributeSchema,
  question_7_option_2_explanation: LocalizedAttributeSchema,
  question_7_option_3: LocalizedAttributeSchema,
  question_7_option_3_explanation: LocalizedAttributeSchema,
  question_7_option_4: LocalizedAttributeSchema,
  question_7_option_4_explanation: LocalizedAttributeSchema
})

const WddMultipleChoiceConditionalFragmentSchema = z.object({
  ...WddSharedAttributes,
  kind: z.literal("wdd_multiple_choice_conditional"),
  points_follow_up: z.coerce.number(),
  solution: z.string(),
  solution_follow_up: z.string(),
  question: LocalizedAttributeSchema,
  option_1: LocalizedAttributeSchema,
  option_1_explanation: LocalizedAttributeSchema,
  option_2: LocalizedAttributeSchema,
  option_2_explanation: LocalizedAttributeSchema,
  option_3: LocalizedAttributeSchema,
  option_3_explanation: LocalizedAttributeSchema,
  //
  follow_up_1: LocalizedAttributeSchema,
  follow_up_1_option_1: LocalizedAttributeSchema,
  follow_up_1_option_1_explanation: LocalizedAttributeSchema,
  follow_up_1_option_2: LocalizedAttributeSchema,
  follow_up_1_option_2_explanation: LocalizedAttributeSchema,
  //
  follow_up_2: LocalizedAttributeSchema,
  follow_up_2_option_1: LocalizedAttributeSchema,
  follow_up_2_option_1_explanation: LocalizedAttributeSchema,
  follow_up_2_option_2: LocalizedAttributeSchema,
  follow_up_2_option_2_explanation: LocalizedAttributeSchema,
  //
  follow_up_3: LocalizedAttributeSchema,
  follow_up_3_option_1: LocalizedAttributeSchema,
  follow_up_3_option_1_explanation: LocalizedAttributeSchema,
  follow_up_3_option_2: LocalizedAttributeSchema,
  follow_up_3_option_2_explanation: LocalizedAttributeSchema
})

const WddFragmentSchema = z.discriminatedUnion("kind", [
  WddOpenSesameFragmentSchema,
  WddMultipleChoiceFragmentSchema,
  WddMultipleChoiceImageFragmentSchema,
  WddMultipleChoiceSequenceFragmentSchema,
  WddMultipleChoiceConditionalFragmentSchema
])

const FragmentSchema = z.discriminatedUnion("kind", [
  BasicFragmentSchema,
  AdvancedFragmentSchema,
  OpenSesameFragmentSchema,
  WddOpenSesameFragmentSchema,
  WddMultipleChoiceFragmentSchema,
  WddMultipleChoiceImageFragmentSchema,
  WddMultipleChoiceSequenceFragmentSchema,
  WddMultipleChoiceConditionalFragmentSchema,
  PostfinanceMultipleChoiceFragmentSchema,
  IframeFragmentSchema
])

const PostfinanceFragmentSchema = z.discriminatedUnion("kind", [
  PostfinanceMultipleChoiceFragmentSchema
])

const IframeSchema = z.discriminatedUnion("kind", [
  IframeFragmentSchema
])


export type Fragment = z.infer<typeof FragmentSchema>
export type WddFragment = z.infer<typeof WddFragmentSchema>
export type PostfinanceFragment = z.infer<typeof PostfinanceFragmentSchema>
export type IframeSchema = z.infer<typeof IframeSchema>

// Challenge

const ChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: FragmentSchema
})

const SceneSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  scanpoint_id: z.coerce.number().nullable(),
  fragment: IframeFragmentSchema
})

const WddChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: WddFragmentSchema
})

const PostfinanceChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: PostfinanceFragmentSchema
})

export type Challenge = z.infer<typeof ChallengeSchema>
export type WddChallenge = z.infer<typeof WddChallengeSchema>
export type PostfinanceChallenge = z.infer<typeof PostfinanceChallengeSchema>

const OpenSesameChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: OpenSesameFragmentSchema
})

// WDD

const WddOpenSesameChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: WddOpenSesameFragmentSchema
})

const WddMultipleChoiceChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: WddMultipleChoiceFragmentSchema
})

const WddMultipleChoiceImageChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: WddMultipleChoiceImageFragmentSchema
})

const WddMultipleChoiceSequenceChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: WddMultipleChoiceSequenceFragmentSchema
})

const WddMultipleChoiceConditionalChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: WddMultipleChoiceConditionalFragmentSchema
})

const PostfinanceMultipleChoiceChallengeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: PostfinanceMultipleChoiceFragmentSchema
})



export type OpenSesameChallenge = z.infer<typeof OpenSesameChallengeSchema>
export type WddOpenSesameChallenge = z.infer<
  typeof WddOpenSesameChallengeSchema
>
export type WddMultipleChoiceChallenge = z.infer<
  typeof WddMultipleChoiceChallengeSchema
>
export type WddMultipleChoiceImageChallenge = z.infer<
  typeof WddMultipleChoiceImageChallengeSchema
>
export type WddMultipleChoiceSequenceChallenge = z.infer<
  typeof WddMultipleChoiceSequenceChallengeSchema
>
export type WddMultipleChoiceConditionalChallenge = z.infer<
  typeof WddMultipleChoiceConditionalChallengeSchema
>
export type PostfinanceMultipleChoiceChallenge = z.infer<
    typeof PostfinanceMultipleChoiceChallengeSchema
>
// Block

const BlockSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  fragment: FragmentSchema
})

export type Block = z.infer<typeof BlockSchema>

// Content Version

export const ContentVersionSchema = z
    .object({
      id: z.string().uuid(),
      quest: z
          .object({
            id: z.coerce.number(),
            name: z.string(),
            paths: z.array(PathSchema),
            challenges: z.array(ChallengeSchema),
            blocks: z.array(BlockSchema),
          })
          .optional(),
      story: z
          .object({
            id: z.coerce.number(),
            name: z.string(),
            blocks: z.array(BlockSchema),
            scenes: z.array(SceneSchema),
          })
          .optional(),
    })
    .refine((data) => data.quest || data.story, {
      message: "Either quest or story must be defined",
    });

export type ContentVersion = z.infer<typeof ContentVersionSchema>

// Activity

export const ActivitySchema = z.object({
  id: z.string().uuid(),
  created_at: z.string(),
  kind: z.string(),
  data: z.record(z.unknown())
})

export const CreateActivitySchema = z.object({
  activity: z.object({
    kind: z.string(),
    data: z.record(z.unknown())
  })
})

export type Activity = z.infer<typeof ActivitySchema>
export type CreateActivity = z.infer<typeof CreateActivitySchema>

// Journey

export const JourneyDataSchema = z.object({
  id: z.string().uuid(),
  group: z.object({
    id: z.string().uuid(),
    path_id: z.coerce.number(),
    ticket: z.object({
      id: z.string().uuid()
    }),
    content_version: z.object({
      id: z.string().uuid()
    }),
    users: z.array(
      z.object({
        id: z.string().uuid()
      })
    ),
    activities: z.array(ActivitySchema),
    data: z.record(z.unknown())
  })
})

export type JourneyData = z.infer<typeof JourneyDataSchema>

// User

export const UserSchema = z.object({
  id: z.string().uuid(),
  token: z.string()
})

export const CreateUserSchema = z.object({
  user: z.object({
    ticket_id: z.string().uuid(),
    quest_name: z.string().optional(),
    story_name: z.string().optional()
  })
})

export type User = z.infer<typeof UserSchema>
export type CreateUser = z.infer<typeof CreateUserSchema>
